import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import { action } from "mobx";
import { observer } from "mobx-react";
import { sortable } from "react-anything-sortable";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import getPath from "../../Core/getPath";
import CatalogMemberMixin, { getName } from "../../ModelMixins/CatalogMemberMixin";
import MappableMixin from "../../ModelMixins/MappableMixin";
import ReferenceMixin from "../../ModelMixins/ReferenceMixin";
import CommonStrata from "../../Models/Definition/CommonStrata";
import Box, { BoxSpan } from "../../Styled/Box";
import { RawButton } from "../../Styled/Button";
import Checkbox from "../../Styled/Checkbox/Checkbox";
import Icon, { StyledIcon } from "../../Styled/Icon";
import { Li } from "../../Styled/List";
import { TextSpan } from "../../Styled/Text";
import Loader from "../Loader";
import PrivateIndicator from "../PrivateIndicator/PrivateIndicator";
import WorkbenchItemControls from "./Controls/WorkbenchItemControls";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const WorkbenchItemRaw = observer(props => {
  const {
    item,
    style,
    className,
    viewState,
    onMouseDown,
    onTouchStart
  } = props;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const toggleDisplay = action(() => {
    if (!CatalogMemberMixin.isMixedInto(item)) return;
    item.setTrait(CommonStrata.user, "isOpenInWorkbench", !item.isOpenInWorkbench);
  });
  const toggleVisibility = action(() => {
    if (MappableMixin.isMixedInto(item)) {
      item.setTrait(CommonStrata.user, "show", !item.show);
    }
  });

  /** If workbench item is CatalogMember use CatalogMemberTraits.isOpenInWorkbench
   * Otherwise, defaults to true
   */
  const isOpen = !CatalogMemberMixin.isMixedInto(item) || item.isOpenInWorkbench;
  const isLoading = CatalogMemberMixin.isMixedInto(item) && item.isLoading || ReferenceMixin.isMixedInto(item) && item.isLoadingReference;
  return /*#__PURE__*/_jsxs(StyledLi, {
    style: style,
    className: className,
    children: [/*#__PURE__*/_jsxs(Box, {
      fullWidth: true,
      justifySpaceBetween: true,
      paddedRatio: 3,
      styledMinHeight: "38px",
      children: [/*#__PURE__*/_jsx(Box, {
        fullWidth: true,
        children: /*#__PURE__*/_jsx(Box, {
          left: true,
          fullWidth: true,
          centered: true,
          children: /*#__PURE__*/_jsxs(DraggableBox, {
            onMouseDown: onMouseDown,
            onTouchStart: onTouchStart,
            title: getPath(item, " → "),
            fullWidth: true,
            children: [!item.isMappable && !isLoading && /*#__PURE__*/_jsx(BoxSpan, {
              paddedHorizontally: true,
              displayInlineBlock: true,
              children: /*#__PURE__*/_jsx(Box, {
                padded: true,
                children: /*#__PURE__*/_jsx(StyledIcon, {
                  styledHeight: "18px",
                  light: true,
                  glyph: Icon.GLYPHS.lineChart
                })
              })
            }), MappableMixin.isMixedInto(item) ? /*#__PURE__*/_jsx(Box, {
              left: true,
              verticalCenter: true,
              children: /*#__PURE__*/_jsx(_StyledCheckbox, {
                id: "workbenchtoggleVisibility",
                isChecked: item.show,
                isSwitch: true,
                title: t("workbench.toggleVisibility"),
                onChange: toggleVisibility,
                textProps: {
                  medium: true,
                  fullWidth: true
                },
                children: /*#__PURE__*/_jsx(TextSpan, {
                  medium: true,
                  maxLines: !isOpen ? 2 : false,
                  title: getName(item),
                  children: getName(item)
                })
              })
            }) : /*#__PURE__*/_jsx(_StyledTextSpan, {
              medium: true,
              textLight: true,
              maxLines: !isOpen ? 2 : false,
              title: getName(item),
              children: getName(item)
            })]
          })
        })
      }), CatalogMemberMixin.isMixedInto(item) ? /*#__PURE__*/_jsxs(Box, {
        centered: true,
        paddedHorizontally: true,
        children: [item.isPrivate && /*#__PURE__*/_jsx(BoxSpan, {
          paddedHorizontally: true,
          children: /*#__PURE__*/_jsx(PrivateIndicator, {
            inWorkbench: true
          })
        }), /*#__PURE__*/_jsx(RawButton, {
          onClick: toggleDisplay,
          children: /*#__PURE__*/_jsx(BoxSpan, {
            padded: true,
            children: isOpen ? /*#__PURE__*/_jsx(StyledIcon, {
              styledHeight: "8px",
              light: true,
              glyph: Icon.GLYPHS.opened
            }) : /*#__PURE__*/_jsx(StyledIcon, {
              styledHeight: "8px",
              light: true,
              glyph: Icon.GLYPHS.closed
            })
          })
        })]
      }) : null]
    }), isOpen && /*#__PURE__*/_jsxs(_StyledBox, {
      column: true,
      gap: 3,
      paddedRatio: 3,
      $_css: "1px solid ".concat(theme.grey),
      $_css2: theme.greyLighter,
      children: [/*#__PURE__*/_jsx(WorkbenchItemControls, {
        item: item,
        viewState: viewState
      }), isLoading ? /*#__PURE__*/_jsx(Box, {
        paddedVertically: true,
        children: /*#__PURE__*/_jsx(Loader, {
          light: true
        })
      }) : null]
    })]
  });
});
WorkbenchItemRaw.displayName = "WorkbenchItem";
const DraggableBox = styled(Box).withConfig({
  displayName: "WorkbenchItem__DraggableBox",
  componentId: "sc-815d2g-0"
})(["cursor:move;"]);
const StyledLi = styled(Li).withConfig({
  displayName: "WorkbenchItem__StyledLi",
  componentId: "sc-815d2g-1"
})(["background:", ";color:", ";border-radius:8px;border:1px solid ", ";width:100%;margin-bottom:20px;&:last-child{margin-bottom:0px;}"], p => p.theme.darkWithOverlay, p => p.theme.textLight, p => p.theme.grey);
export default sortable(WorkbenchItemRaw);
var _StyledCheckbox = styled(Checkbox).withConfig({
  displayName: "WorkbenchItem___StyledCheckbox",
  componentId: "sc-815d2g-2"
})(["overflow-wrap:anywhere;margin-right:5px;"]);
var _StyledTextSpan = styled(TextSpan).withConfig({
  displayName: "WorkbenchItem___StyledTextSpan",
  componentId: "sc-815d2g-3"
})(["overflow-wrap:anywhere;"]);
var _StyledBox = styled(Box).withConfig({
  displayName: "WorkbenchItem___StyledBox",
  componentId: "sc-815d2g-4"
})(p => ({
  borderTop: p.$_css,
  color: p.$_css2
}));